import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Calendar, Upload, Film, FileText, Link as LinkIcon, Plus, Trash2, Play, ExternalLink, File } from 'lucide-react'
import { EVENT_FORMATS, EVENT_STATUSES, INITIAL_EVENT_FORM } from '../constants'
import adminApi from '../../../services/adminApi'
import {
  getMediaUrl,
  validateMediaFile,
  validateMediaUrl,
  getAcceptedMediaTypes,
  getMediaDisplayName,
  formatFileSize
} from '../../../utils/mediaHelpers'
import { toLocalDatetimeString } from '../../../utils/helpers'

const AdminEventModal = ({
  show,
  onClose,
  editingEvent,
  spaces,
  facilitators,
  onSaved,
}) => {
  const [eventForm, setEventForm] = useState(INITIAL_EVENT_FORM)
  const [eventImageFile, setEventImageFile] = useState(null)
  const [eventImagePreview, setEventImagePreview] = useState(null)
  const [savingEvent, setSavingEvent] = useState(false)
  const [eventFormError, setEventFormError] = useState('')
  const [externalFacInput, setExternalFacInput] = useState('')

  // Media state
  const [existingMedia, setExistingMedia] = useState([])
  const [newMediaFiles, setNewMediaFiles] = useState([])
  const [newMediaUrls, setNewMediaUrls] = useState([])
  const [mediaUrlInput, setMediaUrlInput] = useState('')
  const [mediaError, setMediaError] = useState('')
  const [deletedMediaIds, setDeletedMediaIds] = useState([])

  // Post-event resources state
  const [postEventResources, setPostEventResources] = useState([])
  const [resourceForm, setResourceForm] = useState({ title: '', description: '', url: '', resourceType: 'recording', mode: 'link' })
  const [resourceFile, setResourceFile] = useState(null)
  const [resourceError, setResourceError] = useState('')
  const [resourceSaving, setResourceSaving] = useState(false)

  // Populate form when modal opens or editingEvent changes
  useEffect(() => {
    if (show) {
      if (editingEvent) {
        setEventForm({
          name: editingEvent.name,
          eventDate: toLocalDatetimeString(editingEvent.eventDate),
          endDate: toLocalDatetimeString(editingEvent.endDate),
          format: editingEvent.format,
          spaceId: editingEvent.spaceId || '',
          location: editingEvent.location || '',
          venueLink: editingEvent.venueLink || '',
          shortDescription: editingEvent.shortDescription,
          detailedDescription: editingEvent.detailedDescription,
          fee: editingEvent.fee || '',
          registrationLink: editingEvent.registrationLink || '',
          registrationOpen: editingEvent.registrationOpen,
          maxAttendees: editingEvent.maxAttendees || '',
          formType: editingEvent.formType || '',
          paymentGatewayEnabled: editingEvent.paymentGatewayEnabled || false,
          status: editingEvent.status,
          facilitatorIds: editingEvent.facilitators?.map(f => f.id) || [],
          externalFacilitators: editingEvent.externalFacilitators || [],
        })
        setEventImageFile(null)
        setEventImagePreview(editingEvent.image ? `${import.meta.env.VITE_API_URL || ''}${editingEvent.image}` : null)
        // Load existing media
        setExistingMedia(editingEvent.media || [])
        setPostEventResources(editingEvent.postEventResources || [])
      } else {
        setEventForm(INITIAL_EVENT_FORM)
        setEventImageFile(null)
        setEventImagePreview(null)
        setExistingMedia([])
        setPostEventResources([])
      }
      setEventFormError('')
      setExternalFacInput('')
      setNewMediaFiles([])
      setNewMediaUrls([])
      setMediaUrlInput('')
      setMediaError('')
      setDeletedMediaIds([])
      setResourceForm({ title: '', description: '', url: '', resourceType: 'recording', mode: 'link' })
      setResourceFile(null)
      setResourceError('')
    }
  }, [show, editingEvent])

  const handleClose = () => {
    setEventForm(INITIAL_EVENT_FORM)
    setEventImageFile(null)
    setEventImagePreview(null)
    setEventFormError('')
    setExternalFacInput('')
    // Reset media state
    setExistingMedia([])
    setNewMediaFiles([])
    setNewMediaUrls([])
    setMediaUrlInput('')
    setMediaError('')
    setDeletedMediaIds([])
    // Reset post-event resources state
    setPostEventResources([])
    setResourceForm({ title: '', description: '', url: '', resourceType: 'recording', mode: 'link' })
    setResourceFile(null)
    setResourceError('')
    onClose()
  }

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target
    setEventForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    setEventFormError('')
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setEventImageFile(file)
      const reader = new FileReader()
      reader.onloadend = () => setEventImagePreview(reader.result)
      reader.readAsDataURL(file)
    }
  }

  const handleFacilitatorToggle = (profileId) => {
    setEventForm(prev => ({
      ...prev,
      facilitatorIds: prev.facilitatorIds.includes(profileId)
        ? prev.facilitatorIds.filter(id => id !== profileId)
        : [...prev.facilitatorIds, profileId]
    }))
  }

  const handleAddExternalFacilitator = () => {
    if (externalFacInput.trim()) {
      setEventForm(prev => ({
        ...prev,
        externalFacilitators: [...prev.externalFacilitators, externalFacInput.trim()]
      }))
      setExternalFacInput('')
    }
  }

  const handleRemoveExternalFacilitator = (index) => {
    setEventForm(prev => ({
      ...prev,
      externalFacilitators: prev.externalFacilitators.filter((_, i) => i !== index)
    }))
  }

  // Media handling functions
  const handleMediaFilesChange = (e) => {
    const files = Array.from(e.target.files)
    setMediaError('')

    const validFiles = []
    for (const file of files) {
      const validation = validateMediaFile(file)
      if (!validation.valid) {
        setMediaError(validation.error)
        continue
      }
      validFiles.push({
        file,
        type: file.type.startsWith('image/') ? 'image' :
              file.type.startsWith('video/') ? 'video' : 'pdf',
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        name: file.name,
        size: file.size,
      })
    }

    setNewMediaFiles(prev => [...prev, ...validFiles])
    e.target.value = ''
  }

  const handleAddMediaUrl = () => {
    setMediaError('')
    const validation = validateMediaUrl(mediaUrlInput)

    if (!validation.valid) {
      setMediaError(validation.error)
      return
    }

    setNewMediaUrls(prev => [...prev, {
      url: mediaUrlInput.trim(),
      type: validation.type,
    }])
    setMediaUrlInput('')
  }

  const handleRemoveNewMediaFile = (index) => {
    setNewMediaFiles(prev => {
      const updated = [...prev]
      if (updated[index].preview) {
        URL.revokeObjectURL(updated[index].preview)
      }
      updated.splice(index, 1)
      return updated
    })
  }

  const handleRemoveNewMediaUrl = (index) => {
    setNewMediaUrls(prev => prev.filter((_, i) => i !== index))
  }

  const handleMarkExistingMediaForDeletion = (mediaId) => {
    setDeletedMediaIds(prev => [...prev, mediaId])
    setExistingMedia(prev => prev.filter(m => m.id !== mediaId))
  }

  const uploadNewMedia = async (eventId) => {
    // Upload files
    if (newMediaFiles.length > 0) {
      const formData = new FormData()
      newMediaFiles.forEach(item => {
        formData.append('media', item.file)
      })

      try {
        await adminApi.post(`/api/admin/events/${eventId}/media`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      } catch (err) {
        console.error('Error uploading media files:', err)
      }
    }

    // Add URLs
    for (const urlItem of newMediaUrls) {
      try {
        await adminApi.post(`/api/admin/events/${eventId}/media`, {
          url: urlItem.url,
        })
      } catch (err) {
        console.error('Error adding media URL:', err)
      }
    }

    // Delete marked media
    for (const mediaId of deletedMediaIds) {
      try {
        await adminApi.delete(`/api/admin/events/${eventId}/media/${mediaId}`)
      } catch (err) {
        console.error('Error deleting media:', err)
      }
    }
  }

  // Post-event resource handlers
  const handleAddResource = async () => {
    if (!editingEvent?.id) return
    setResourceError('')
    setResourceSaving(true)

    try {
      if (resourceForm.mode === 'file' && resourceFile) {
        const formData = new FormData()
        formData.append('file', resourceFile)
        formData.append('title', resourceForm.title || resourceFile.name)
        if (resourceForm.description) formData.append('description', resourceForm.description)
        if (resourceForm.resourceType) formData.append('resourceType', resourceForm.resourceType)

        const res = await adminApi.post(`/api/admin/events/${editingEvent.id}/post-event-resources`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        setPostEventResources(prev => [...prev, res.data.data])
      } else if (resourceForm.mode === 'link' && resourceForm.url.trim()) {
        if (!resourceForm.title.trim()) {
          setResourceError('Title is required')
          setResourceSaving(false)
          return
        }
        const res = await adminApi.post(`/api/admin/events/${editingEvent.id}/post-event-resources`, {
          title: resourceForm.title,
          description: resourceForm.description || null,
          url: resourceForm.url,
          resourceType: resourceForm.resourceType,
        })
        setPostEventResources(prev => [...prev, res.data.data])
      } else {
        setResourceError(resourceForm.mode === 'file' ? 'Please select a file' : 'Please enter a URL')
        setResourceSaving(false)
        return
      }
      setResourceForm({ title: '', description: '', url: '', resourceType: 'recording', mode: resourceForm.mode })
      setResourceFile(null)
    } catch (err) {
      setResourceError(err.response?.data?.message || 'Failed to add resource')
    } finally {
      setResourceSaving(false)
    }
  }

  const handleDeleteResource = async (resourceId) => {
    if (!editingEvent?.id) return
    try {
      await adminApi.delete(`/api/admin/events/${editingEvent.id}/post-event-resources/${resourceId}`)
      setPostEventResources(prev => prev.filter(r => r.id !== resourceId))
    } catch (err) {
      console.error('Error deleting resource:', err)
    }
  }

  const getResourceIcon = (resourceType) => {
    switch (resourceType) {
      case 'recording': return <Play className="w-4 h-4" />
      case 'pdf': return <FileText className="w-4 h-4" />
      case 'document': return <File className="w-4 h-4" />
      default: return <ExternalLink className="w-4 h-4" />
    }
  }

  const getResourceBadgeColor = (resourceType) => {
    switch (resourceType) {
      case 'recording': return 'bg-red-500/20 text-red-300'
      case 'pdf': return 'bg-orange-500/20 text-orange-300'
      case 'document': return 'bg-blue-500/20 text-blue-300'
      default: return 'bg-gray-500/20 text-gray-300'
    }
  }

  const handleSaveEvent = async (e) => {
    e.preventDefault()
    setEventFormError('')
    setSavingEvent(true)

    try {
      const formData = new FormData()
      
      formData.append('name', eventForm.name)
      formData.append('eventDate', new Date(eventForm.eventDate).toISOString())
      if (eventForm.endDate) formData.append('endDate', new Date(eventForm.endDate).toISOString())
      formData.append('format', eventForm.format)
      if (eventForm.spaceId) formData.append('spaceId', eventForm.spaceId)
      if (eventForm.location) formData.append('location', eventForm.location)
      if (eventForm.venueLink) formData.append('venueLink', eventForm.venueLink)
      formData.append('shortDescription', eventForm.shortDescription)
      formData.append('detailedDescription', eventForm.detailedDescription)
      if (eventForm.fee) formData.append('fee', eventForm.fee)
      // Always send so clearing the field clears DB (backend only updates when field is present)
      formData.append('registrationLink', (eventForm.registrationLink || '').trim())
      formData.append('registrationOpen', eventForm.registrationOpen)
      if (eventForm.maxAttendees) formData.append('maxAttendees', eventForm.maxAttendees)
      formData.append('formType', eventForm.formType || '')
      formData.append('paymentGatewayEnabled', eventForm.paymentGatewayEnabled)
      formData.append('status', eventForm.status)
      
      if (eventForm.facilitatorIds.length > 0) {
        formData.append('facilitatorIds', JSON.stringify(eventForm.facilitatorIds))
      }
      if (eventForm.externalFacilitators.length > 0) {
        formData.append('externalFacilitators', JSON.stringify(eventForm.externalFacilitators))
      }
      if (eventImageFile) {
        formData.append('image', eventImageFile)
      }

      let eventId = editingEvent?.id

      if (editingEvent) {
        await adminApi.put(`/api/admin/events/${editingEvent.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      } else {
        const response = await adminApi.post('/api/admin/events', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        eventId = response.data.data?.id || response.data.id
      }

      // Upload new media and delete marked media
      if (eventId && (newMediaFiles.length > 0 || newMediaUrls.length > 0 || deletedMediaIds.length > 0)) {
        await uploadNewMedia(eventId)
      }

      handleClose()
      onSaved()
    } catch (err) {
      setEventFormError(err.response?.data?.message || 'Failed to save event')
    } finally {
      setSavingEvent(false)
    }
  }

  return createPortal(
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={handleClose}
          />

          <motion.div
            className="relative bg-slate-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden border border-white/20"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Calendar className="w-6 h-6 text-purple-400" />
                {editingEvent ? 'Edit Event' : 'Create New Event'}
              </h2>
              <button
                onClick={handleClose}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <form onSubmit={handleSaveEvent} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="space-y-6">
                {/* Event Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Event Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={eventForm.name}
                    onChange={handleFormChange}
                    placeholder="e.g., Morning Yoga Flow"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Start Date & Time <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      name="eventDate"
                      value={eventForm.eventDate}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">End Date & Time</label>
                    <input
                      type="datetime-local"
                      name="endDate"
                      value={eventForm.endDate}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>

                {/* Format & Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Format <span className="text-red-400">*</span></label>
                    <select
                      name="format"
                      value={eventForm.format}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 bg-slate-700 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 [&>option]:bg-slate-700"
                    >
                      {EVENT_FORMATS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Status <span className="text-red-400">*</span></label>
                    <select
                      name="status"
                      value={eventForm.status}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 bg-slate-700 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 [&>option]:bg-slate-700"
                    >
                      {EVENT_STATUSES.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Location */}
                {(eventForm.format === 'in-person' || eventForm.format === 'hybrid') && (
                  <div className="space-y-4 p-4 bg-white/5 rounded-xl">
                    <h3 className="font-semibold text-white">Location</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Select a Space (optional)</label>
                      <select
                        name="spaceId"
                        value={eventForm.spaceId}
                        onChange={handleFormChange}
                        className="w-full px-4 py-3 bg-slate-700 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 [&>option]:bg-slate-700"
                      >
                        <option value="">-- Use custom location --</option>
                        {spaces?.map(space => (
                          <option key={space.id} value={space.id}>{space.name} {space.city && `(${space.city})`}</option>
                        ))}
                      </select>
                    </div>
                    {!eventForm.spaceId && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-2">Custom Location</label>
                          <input
                            type="text"
                            name="location"
                            value={eventForm.location}
                            onChange={handleFormChange}
                            placeholder="e.g., Community Center, 123 Main St"
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-2">Venue Link</label>
                          <input
                            type="url"
                            name="venueLink"
                            value={eventForm.venueLink}
                            onChange={handleFormChange}
                            placeholder="https://maps.google.com/..."
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Descriptions */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Short Description <span className="text-red-400">*</span></label>
                  <textarea
                    name="shortDescription"
                    value={eventForm.shortDescription}
                    onChange={handleFormChange}
                    placeholder="Brief overview of your event"
                    rows={2}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Detailed Description <span className="text-red-400">*</span></label>
                  <textarea
                    name="detailedDescription"
                    value={eventForm.detailedDescription}
                    onChange={handleFormChange}
                    placeholder="Full details about your event..."
                    rows={4}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    required
                  />
                </div>

                {/* Fee & Max Attendees */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Fee</label>
                    <input
                      type="text"
                      name="fee"
                      value={eventForm.fee}
                      onChange={handleFormChange}
                      placeholder="e.g., ₹599 or 'Free'"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Max Attendees</label>
                    <input
                      type="number"
                      name="maxAttendees"
                      value={eventForm.maxAttendees}
                      onChange={handleFormChange}
                      placeholder="Leave empty for unlimited"
                      min="1"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>

                {/* Registration */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">External Registration Link</label>
                  <input
                    type="url"
                    name="registrationLink"
                    value={eventForm.registrationLink}
                    onChange={handleFormChange}
                    placeholder="https://..."
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    name="registrationOpen"
                    checked={eventForm.registrationOpen}
                    onChange={handleFormChange}
                    id="adminRegOpen"
                    className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500 bg-slate-700 border-white/10"
                  />
                  <label htmlFor="adminRegOpen" className="text-sm text-gray-300">Registration is open on the platform</label>
                </div>

                {eventForm.fee && (
                  <div>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        name="paymentGatewayEnabled"
                        checked={eventForm.paymentGatewayEnabled}
                        onChange={handleFormChange}
                        id="adminPaymentGateway"
                        className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500 bg-slate-700 border-white/10"
                      />
                      <label htmlFor="adminPaymentGateway" className="text-sm text-gray-300">Enable Razorpay Payment Gateway</label>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 ml-8">When enabled, users pay via Razorpay checkout instead of manual QR code + transaction ID</p>
                  </div>
                )}

                {/* Registration Form Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Registration Form Type</label>
                  <select
                    name="formType"
                    value={eventForm.formType}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 bg-slate-700 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 [&>option]:bg-slate-700"
                  >
                    <option value="">Standard (Name, Email, Phone)</option>
                    <option value="extended-workshop">Extended Workshop Form (+ Job Title, Org, Goals, Referral)</option>
                    <option value="extended-form2">Extended Form 2 (Standard fields + Payment QR + Transaction ID)</option>
                    <option value="family-intake">Family intake (parent/child fields, no payment)</option>
                    <option value="unmasking-halfday-apr2026">Unmasking half-day (age, gender, company, session, UPI, txn ID)</option>
                    <option value="a-way-to-move">A Way To Move (slot enquiry, no payment)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Controls which registration form guests and users see for this event</p>
                </div>

                {/* Facilitators */}
                <div className="space-y-4 p-4 bg-blue-500/10 rounded-xl">
                  <h3 className="font-semibold text-white">Facilitators (from platform)</h3>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {facilitators?.map(fac => (
                      <label key={fac.profileId} className="flex items-center gap-3 p-2 hover:bg-white/5 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={eventForm.facilitatorIds.includes(fac.profileId)}
                          onChange={() => handleFacilitatorToggle(fac.profileId)}
                          className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500 bg-slate-700 border-white/10"
                        />
                        <span className="text-sm text-gray-300">{fac.name} <span className="text-gray-500">({fac.email})</span></span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* External Facilitators */}
                <div className="space-y-4 p-4 bg-amber-500/10 rounded-xl">
                  <h3 className="font-semibold text-white">External Facilitators</h3>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={externalFacInput}
                      onChange={(e) => setExternalFacInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddExternalFacilitator())}
                      placeholder="Enter facilitator name"
                      className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                      type="button"
                      onClick={handleAddExternalFacilitator}
                      className="px-4 py-2 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  {eventForm.externalFacilitators.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {eventForm.externalFacilitators.map((name, index) => (
                        <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-amber-500/30 text-amber-200 rounded-full text-sm">
                          {name}
                          <button type="button" onClick={() => handleRemoveExternalFacilitator(index)} className="hover:text-white">
                            <X className="w-4 h-4" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Event Cover Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Event Cover Image</label>
                  <div className="border-2 border-dashed border-white/20 rounded-xl p-4">
                    {eventImagePreview ? (
                      <div className="relative">
                        <img src={eventImagePreview} alt="Preview" className="w-full h-40 object-cover rounded-lg" />
                        <button
                          type="button"
                          onClick={() => { setEventImageFile(null); setEventImagePreview(null); }}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center h-32 cursor-pointer">
                        <Upload className="w-8 h-8 text-gray-500 mb-2" />
                        <span className="text-sm text-gray-400">Click to upload image</span>
                        <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                      </label>
                    )}
                  </div>
                </div>

                {/* Additional Media Gallery */}
                <div className="space-y-4 p-4 bg-indigo-500/10 rounded-xl">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-white">Additional Media</h3>
                    <span className="text-sm text-gray-400">
                      Images, Videos, PDFs
                    </span>
                  </div>
                  <p className="text-sm text-gray-400">
                    Add additional media files to this event (photos, videos, PDFs). Upload files or paste URLs (YouTube, Vimeo, etc.).
                  </p>

                  {/* Existing Media */}
                  {existingMedia.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-gray-300">Current Media</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {existingMedia.map((media) => (
                          <div
                            key={media.id}
                            className="relative aspect-square rounded-lg overflow-hidden bg-white/5 group"
                          >
                            {media.type === 'image' && (
                              <img
                                src={getMediaUrl(media.url)}
                                alt={media.caption || 'Media'}
                                className="w-full h-full object-cover"
                              />
                            )}
                            {media.type === 'video' && (
                              <div className="w-full h-full flex flex-col items-center justify-center bg-slate-700 text-white">
                                <Film className="w-8 h-8 mb-1" />
                                <span className="text-xs">Video</span>
                              </div>
                            )}
                            {media.type === 'pdf' && (
                              <div className="w-full h-full flex flex-col items-center justify-center bg-red-500/20">
                                <FileText className="w-8 h-8 text-red-400 mb-1" />
                                <span className="text-xs text-red-300 px-1 text-center line-clamp-2">
                                  {getMediaDisplayName(media)}
                                </span>
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={() => handleMarkExistingMediaForDeletion(media.id)}
                              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                              title="Remove"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* New Media Files Preview */}
                  {newMediaFiles.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-gray-300">New Files to Upload</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {newMediaFiles.map((item, index) => (
                          <div
                            key={index}
                            className="relative aspect-square rounded-lg overflow-hidden bg-white/5 group border-2 border-green-500/40"
                          >
                            {item.type === 'image' && item.preview && (
                              <img
                                src={item.preview}
                                alt={item.name}
                                className="w-full h-full object-cover"
                              />
                            )}
                            {item.type === 'video' && (
                              <div className="w-full h-full flex flex-col items-center justify-center bg-slate-700 text-white">
                                <Film className="w-8 h-8 mb-1" />
                                <span className="text-xs px-1 text-center line-clamp-2">{item.name}</span>
                              </div>
                            )}
                            {item.type === 'pdf' && (
                              <div className="w-full h-full flex flex-col items-center justify-center bg-red-500/20">
                                <FileText className="w-8 h-8 text-red-400 mb-1" />
                                <span className="text-xs text-red-300 px-1 text-center line-clamp-2">{item.name}</span>
                              </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 bg-green-600 text-white text-xs py-0.5 text-center">
                              {formatFileSize(item.size)}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveNewMediaFile(index)}
                              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                              title="Remove"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* New Media URLs Preview */}
                  {newMediaUrls.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-gray-300">New URLs to Add</h4>
                      <div className="space-y-2">
                        {newMediaUrls.map((item, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 p-2 bg-white/5 rounded-lg border border-green-500/30"
                          >
                            <span className="text-lg">
                              {item.type === 'video' && '🎬'}
                              {item.type === 'image' && '🖼️'}
                              {item.type === 'pdf' && '📄'}
                            </span>
                            <span className="flex-1 text-sm text-gray-300 truncate">{item.url}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveNewMediaUrl(index)}
                              className="p-1 text-red-400 hover:bg-red-500/20 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add Media Controls */}
                  <div className="space-y-3">
                    {/* File Upload */}
                    <div>
                      <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-indigo-400/40 rounded-xl cursor-pointer hover:bg-indigo-500/10 transition-colors">
                        <Upload className="w-5 h-5 text-indigo-400" />
                        <span className="text-sm font-medium text-indigo-400">Upload Files</span>
                        <input
                          type="file"
                          accept={getAcceptedMediaTypes()}
                          multiple
                          onChange={handleMediaFilesChange}
                          className="hidden"
                        />
                      </label>
                      <p className="text-xs text-gray-500 mt-1 text-center">
                        Images, Videos (MP4, WebM), PDFs - Max 50MB each
                      </p>
                    </div>

                    {/* URL Input */}
                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <input
                          type="url"
                          value={mediaUrlInput}
                          onChange={(e) => setMediaUrlInput(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddMediaUrl())}
                          placeholder="Paste URL (YouTube, Vimeo, image, PDF...)"
                          className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleAddMediaUrl}
                        disabled={!mediaUrlInput.trim()}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  {/* Media Error */}
                  {mediaError && (
                    <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                      {mediaError}
                    </div>
                  )}
                </div>

                {/* Post-Event Resources (only when editing) */}
                {editingEvent && (
                  <div className="space-y-4 p-4 bg-emerald-500/10 rounded-xl">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-white">Post-Event Resources</h3>
                      <span className="text-sm text-gray-400">
                        Recordings, PDFs, Links
                      </span>
                    </div>
                    <p className="text-sm text-gray-400">
                      Share session recordings, handouts, and other resources with attendees after the event.
                    </p>

                    {/* Existing Resources */}
                    {postEventResources.length > 0 && (
                      <div className="space-y-2">
                        {postEventResources.map((resource) => (
                          <div key={resource.id} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg group">
                            <span className={`p-1.5 rounded ${getResourceBadgeColor(resource.resourceType)}`}>
                              {getResourceIcon(resource.resourceType)}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-white truncate">{resource.title}</p>
                              {resource.description && (
                                <p className="text-xs text-gray-400 truncate">{resource.description}</p>
                              )}
                              <p className="text-xs text-gray-500 truncate">
                                {resource.type === 'link' ? resource.url : resource.originalName}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleDeleteResource(resource.id)}
                              className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors opacity-0 group-hover:opacity-100"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add Resource Form */}
                    <div className="space-y-3 p-3 bg-white/5 rounded-lg">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setResourceForm(prev => ({ ...prev, mode: 'link' }))}
                          className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                            resourceForm.mode === 'link' ? 'bg-emerald-600 text-white' : 'bg-white/10 text-gray-400 hover:text-white'
                          }`}
                        >
                          Link / URL
                        </button>
                        <button
                          type="button"
                          onClick={() => setResourceForm(prev => ({ ...prev, mode: 'file' }))}
                          className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                            resourceForm.mode === 'file' ? 'bg-emerald-600 text-white' : 'bg-white/10 text-gray-400 hover:text-white'
                          }`}
                        >
                          Upload File
                        </button>
                      </div>

                      <input
                        type="text"
                        value={resourceForm.title}
                        onChange={(e) => setResourceForm(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Resource title"
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      />

                      {resourceForm.mode === 'link' ? (
                        <input
                          type="url"
                          value={resourceForm.url}
                          onChange={(e) => setResourceForm(prev => ({ ...prev, url: e.target.value }))}
                          placeholder="https://youtube.com/... or https://drive.google.com/..."
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                        />
                      ) : (
                        <div>
                          <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-emerald-400/40 rounded-lg cursor-pointer hover:bg-emerald-500/10 transition-colors">
                            <Upload className="w-4 h-4 text-emerald-400" />
                            <span className="text-sm text-emerald-400">
                              {resourceFile ? resourceFile.name : 'Choose file'}
                            </span>
                            <input
                              type="file"
                              accept="video/*,application/pdf,image/*,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
                              onChange={(e) => {
                                const file = e.target.files[0]
                                if (file) {
                                  setResourceFile(file)
                                  if (!resourceForm.title) {
                                    setResourceForm(prev => ({ ...prev, title: file.name }))
                                  }
                                }
                              }}
                              className="hidden"
                            />
                          </label>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-2">
                        <select
                          value={resourceForm.resourceType}
                          onChange={(e) => setResourceForm(prev => ({ ...prev, resourceType: e.target.value }))}
                          className="px-3 py-2 bg-slate-700 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 [&>option]:bg-slate-700"
                        >
                          <option value="recording">Recording</option>
                          <option value="pdf">PDF</option>
                          <option value="document">Document</option>
                          <option value="other">Other</option>
                        </select>
                        <input
                          type="text"
                          value={resourceForm.description}
                          onChange={(e) => setResourceForm(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Description (optional)"
                          className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={handleAddResource}
                        disabled={resourceSaving}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 text-sm font-medium"
                      >
                        <Plus className="w-4 h-4" />
                        {resourceSaving ? 'Adding...' : 'Add Resource'}
                      </button>

                      {resourceError && (
                        <p className="text-red-400 text-sm">{resourceError}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Error */}
                {eventFormError && (
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
                    {eventFormError}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex gap-3 mt-6 pt-6 border-t border-white/10">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-6 py-3 bg-white/10 text-white rounded-xl font-medium hover:bg-white/20 transition-colors"
                  disabled={savingEvent}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingEvent}
                  className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  {savingEvent ? 'Saving...' : (editingEvent ? 'Update Event' : 'Create Event')}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  )
}

export default AdminEventModal
